/**
 * Data lineage. See DATA_MODEL.md section 3 and CLAUDE.md 3.2 and 13.
 *
 * Every `core` row carries these columns so that "where did this number come from?" is always
 * answerable. That is a functional requirement, not documentation.
 */
import { z } from 'zod';
/**
 * How a value came to exist.
 *
 * - `observed`      — normalized from a `raw` payload received from a source system
 * - `calculated`    — computed by deterministic code (`packages/finance`, `derived` views)
 * - `ai_inferred`   — produced by a model. Never written to `core` without an approval.
 * - `human_entered` — a person typed it, or approved an AI inference into place
 */
export const DERIVATIONS = ['observed', 'calculated', 'ai_inferred', 'human_entered'];
export const derivationSchema = z.enum(DERIVATIONS);
export const APPROVAL_STATUSES = ['not_required', 'pending', 'approved', 'rejected'];
export const approvalStatusSchema = z.enum(APPROVAL_STATUSES);
/** Whether a mirrored row has been pushed back to its external system of record. */
export const SYNC_STATUSES = ['not_applicable', 'pending', 'synced', 'drifted', 'failed'];
export const syncStatusSchema = z.enum(SYNC_STATUSES);
export const CONFIDENCE_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];
export const confidenceLevelSchema = z.enum(CONFIDENCE_LEVELS);
/**
 * How a figure should be labelled when displayed. FINANCE.md 8.1: never present estimated
 * information as actual accounting results.
 */
export const FIGURE_LABELS = ['ACTUAL', 'ESTIMATED', 'FORECAST', 'PENDING_CLASSIFICATION'];
/**
 * Build the lineage columns for a new `core` row.
 *
 * Refuses to produce `ai_inferred` lineage without an `aiRunId`. An AI-derived value whose run
 * cannot be identified is unauditable, which defeats the purpose of the column.
 */
export function buildLineage(input) {
    if (input.derivation === 'ai_inferred' && !input.aiRunId) {
        throw new Error('Lineage: derivation "ai_inferred" requires an aiRunId (DATA_MODEL.md 3).');
    }
    if (input.derivation === 'observed' && !input.sourceRecordId) {
        throw new Error('Lineage: derivation "observed" requires a sourceRecordId — an observed fact came from ' +
            'somewhere identifiable (DATA_MODEL.md 3).');
    }
    const now = input.now ?? new Date();
    return {
        sourceSystem: input.sourceSystem,
        sourceRecordId: input.sourceRecordId ?? null,
        rawId: input.rawId ?? null,
        ingestedAt: now,
        updatedAt: now,
        derivation: input.derivation,
        aiRunId: input.aiRunId ?? null,
        confidence: input.confidence ?? null,
        approvalStatus: input.approvalStatus ?? 'not_required',
        approvedBy: input.approvedBy ?? null,
        approvedAt: input.approvedAt ?? null,
        externalSyncStatus: input.externalSyncStatus ?? 'not_applicable',
    };
}
/** Lineage for a row a person created directly through the UI. The Phase 1 case. */
export function humanLineage(sourceSystem = 'sixgen', now) {
    return buildLineage({ sourceSystem, derivation: 'human_entered', now });
}
//# sourceMappingURL=lineage.js.map